// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext, Parliamentarian } from '../types';
import { partyStyle } from '../parties';
import { fmtDollars, escapeHtml } from '../format';
import { partyPill, chamberTag, seatLabel } from '../ui';
import { sparklineSvg } from '../charts';

interface EState {
  q: string;
  chamber: string;
  party: string;
  current: boolean;
  sort: string; // total | name | party | chamber
  dir: 'asc' | 'desc';
}
const DEFAULT: EState = { q: '', chamber: 'all', party: 'ALL', current: false, sort: 'total', dir: 'desc' };

function chamberPass(p: Parliamentarian, c: string): boolean {
  if (c === 'all') return true;
  if (c === 'parl') return p.chamber === 'House' || p.chamber === 'Senate';
  if (c === 'other') return p.chamber === 'Former PM' || p.chamber === 'Other';
  return p.chamber === c;
}

function topCategory(p: Parliamentarian): string {
  let best = '';
  let bv = -Infinity;
  for (const [k, v] of Object.entries(p.byCategory)) if (v > bv) { bv = v; best = k; }
  return best;
}

export function renderExplorer(root: HTMLElement, ctx: AppContext): void {
  const { data } = ctx;
  const s: EState = { ...DEFAULT };
  const parties = data.summary.parties;

  root.innerHTML = `
    <div class="view-head">
      <h2>Explore every parliamentarian</h2>
      <p>Search for any MP or Senator by name or electorate, sort by any column, and click through to a full breakdown. ${data.summary.totals.people} people across ${data.summary.meta.quarterCount} quarters.</p>
    </div>
    <div class="filters">
      <div class="filter"><label for="e-q">Search</label><input type="search" id="e-q" placeholder="Name or electorate…" autocomplete="off" /></div>
      <div class="filter"><label for="e-chamber">Chamber</label>
        <select id="e-chamber">
          <option value="all">All</option>
          <option value="parl">MPs &amp; Senators</option>
          <option value="House">House</option>
          <option value="Senate">Senate</option>
          <option value="other">Former PMs &amp; others</option>
        </select></div>
      <div class="filter"><label for="e-party">Party</label>
        <select id="e-party"><option value="ALL">All parties</option>${parties
          .map((p) => `<option value="${p.code}">${escapeHtml(p.name)}</option>`)
          .join('')}</select></div>
      <label class="check"><input type="checkbox" id="e-current" /> Current only</label>
      <div class="filter" style="margin-left:auto;align-self:flex-end"><span id="e-count" class="rowsub"></span></div>
    </div>
    <div class="table-wrap">
      <table class="data">
        <thead><tr>
          <th class="sortable" data-sort="name">Parliamentarian</th>
          <th class="sortable" data-sort="party">Party</th>
          <th class="sortable" data-sort="chamber">Chamber</th>
          <th>Seat</th>
          <th class="num sortable" data-sort="total">Total</th>
          <th>Trend</th>
          <th>Top category</th>
        </tr></thead>
        <tbody id="e-body"></tbody>
      </table>
    </div>`;

  const body = document.getElementById('e-body')!;
  const countEl = document.getElementById('e-count')!;

  const draw = () => {
    let list = data.people.filter(
      (p) =>
        chamberPass(p, s.chamber) &&
        (s.party === 'ALL' || p.partyCode === s.party) &&
        (!s.current || p.isCurrent)
    );
    const q = s.q.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.electorate || '').toLowerCase().includes(q) ||
          (p.state || '').toLowerCase().includes(q) ||
          p.party.toLowerCase().includes(q)
      );
    }
    const dir = s.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      let c = 0;
      switch (s.sort) {
        case 'name': c = a.name.localeCompare(b.name); break;
        case 'party': c = a.party.localeCompare(b.party) || b.total - a.total; break;
        case 'chamber': c = a.chamber.localeCompare(b.chamber) || b.total - a.total; break;
        default: c = a.total - b.total;
      }
      return c * dir;
    });

    countEl.textContent = `${list.length} shown`;
    document.querySelectorAll('th.sortable').forEach((th) => {
      th.classList.remove('sort-asc', 'sort-desc');
      if ((th as HTMLElement).dataset.sort === s.sort) th.classList.add(s.dir === 'asc' ? 'sort-asc' : 'sort-desc');
    });

    if (!list.length) {
      body.innerHTML = `<tr><td colspan="7"><div class="state-msg">No matches.</div></td></tr>`;
      return;
    }
    body.innerHTML = list
      .map((p) => {
        const col = partyStyle(p.partyCode).colour;
        const spark = sparklineSvg(p.quarterly.map((x) => x.amount), 96, 26, col);
        return `<tr data-slug="${p.slug}">
          <td class="name" data-th="Name">${escapeHtml(p.name)}${p.isCurrent ? '' : ' <span class="rowsub">(former)</span>'}</td>
          <td data-th="Party">${partyPill(p.partyCode)}</td>
          <td data-th="Chamber">${chamberTag(p.chamber)}</td>
          <td data-th="Seat"><span class="rowsub">${seatLabel(p)}</span></td>
          <td class="num total" data-th="Total">${fmtDollars(p.total)}</td>
          <td class="sparkcell" data-tip="Quarterly spend, ${escapeHtml(p.quarterly[0]?.q || '')}–${escapeHtml(p.quarterly[p.quarterly.length - 1]?.q || '')}">${spark}</td>
          <td data-th="Top category"><span class="rowsub">${escapeHtml(topCategory(p))}</span></td>
        </tr>`;
      })
      .join('');
  };

  body.addEventListener('click', (e) => {
    const tr = (e.target as Element).closest<HTMLElement>('tr[data-slug]');
    if (tr?.dataset.slug) ctx.openMp(tr.dataset.slug);
  });
  document.querySelectorAll<HTMLElement>('th.sortable').forEach((th) => {
    th.addEventListener('click', () => {
      const col = th.dataset.sort!;
      if (s.sort === col) s.dir = s.dir === 'asc' ? 'desc' : 'asc';
      else { s.sort = col; s.dir = col === 'name' || col === 'party' || col === 'chamber' ? 'asc' : 'desc'; }
      draw();
    });
  });

  let t: number | undefined;
  document.getElementById('e-q')!.addEventListener('input', (e) => {
    const v = (e.target as HTMLInputElement).value;
    window.clearTimeout(t);
    t = window.setTimeout(() => { s.q = v; draw(); }, 300);
  });
  document.getElementById('e-chamber')!.addEventListener('change', (e) => { s.chamber = (e.target as HTMLSelectElement).value; draw(); });
  document.getElementById('e-party')!.addEventListener('change', (e) => { s.party = (e.target as HTMLSelectElement).value; draw(); });
  document.getElementById('e-current')!.addEventListener('change', (e) => { s.current = (e.target as HTMLInputElement).checked; draw(); });

  draw();
}
